import { Adapt, Button, Icon, Select, Sheet, Text } from '@red-ui/components'
import { getString } from '@vrw/data'
import { RewardsSort as RewardsSortType } from '@vrw/data/src/redux/types'
import React, { FC, memo, useState } from 'react'
import { pushToGTM } from '../../../helpers/windowMethods'

export interface RewardsSortProps {
  sort: RewardsSortType | undefined
  setSort: (value: RewardsSortType | undefined) => void
  sortOptions: Array<{ key: RewardsSortType | undefined; label: string }>
}

const DEFAULT_KEY = 'default' as const

interface SortItemProps {
  optionKey?: RewardsSortType
  label: string
  isActive: boolean
  onSelect: (key?: RewardsSortType) => void
  index: number
}

export const SortItem: FC<SortItemProps> = memo(function SortItem({ optionKey, label, isActive, onSelect, index }: SortItemProps) {
  return (
    <Select.Item
      role="listitem"
      data-testid={`sort-item-${optionKey ?? DEFAULT_KEY}`}
      value={optionKey ?? DEFAULT_KEY}
      index={index}
      borderTopWidth="$0"
      height="$6"
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      backgroundColor={isActive ? '$black1' : 'transparent'}
      onPress={() => onSelect(optionKey)}>
      <Select.ItemHeadingText fontSize="$4" fontWeight="400" $gtMobile={{ fontWeight: '400' }} $gtTabletLandscape={{ fontSize: '$4' }}>
        {label}
      </Select.ItemHeadingText>
      {isActive ? (
        <Icon.RadioButtonSelected color="$red6" size="$2" scale={1.3} />
      ) : (
        <Icon.RadioButton color="$black2" size="$2" scale={1.3} />
      )}
    </Select.Item>
  )
})

export const RewardsSort: FC<RewardsSortProps> = ({ sort, setSort, sortOptions }) => {
  const [isVisible, setVisible] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  const handleSelect = (newSort?: RewardsSortType) => {
    pushToGTM({
      event: 'selectDiscoverySort',
      sortDetails: {
        oldSort: sort ?? DEFAULT_KEY,
        newSort: newSort ?? DEFAULT_KEY,
      },
    })
    setSort(newSort)
    setVisible(false)
  }

  const currentLabel = sortOptions.find((o) => o.key === sort)?.label ?? ''
  return (
    <Select
      open={isVisible}
      onOpenChange={setVisible}
      value={sort ?? DEFAULT_KEY}
      paddingHorizontal={0}
      onValueChange={(val) => handleSelect(val === DEFAULT_KEY ? undefined : (val as RewardsSortType))}>
      <Select.Trigger
        onPress={() => setVisible((v) => !v)}
        width="fit-content"
        height="$6"
        iconAfter={<Icon.ArrowUpDown size={18} color={isHovered || isVisible ? '$red6' : '$black'} />}
        borderColor={isVisible ? '$red6' : '$borderColor'}
        hoverStyle={{ borderColor: '$red6' }}
        onHoverIn={() => setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}
        hoverTheme={false}
        data-testid="sort">
        <Text fontSize="$4" color={isHovered || isVisible ? '$red6' : '$black'}>
          {getString('sort.title') + currentLabel}
        </Text>
      </Select.Trigger>

      <Adapt when="mobile" platform="touch">
        <Sheet snapPoints={[40]} modal dismissOnOverlayPress animationConfig={{ type: 'spring', damping: 20, mass: 1.2, stiffness: 250 }}>
          <Sheet.Overlay backgroundColor="$black10" opacity={0.32} />
          <Sheet.Frame>
            <Select.Label height="$9" alignItems="center" justifyContent="space-between" display="flex" marginVertical="$0" fontSize="$5">
              {getString('rewardDetails.variant.label')}
              <Button
                aria-label={getString('navigation.menu.button.user.close.ariaLabel')}
                backgroundColor="$transparent"
                borderStyle="unset"
                borderWidth={0}
                paddingRight={0}
                paddingLeft={0}
                hoverStyle={{
                  backgroundColor: '$transparent',
                }}
                pressStyle={{
                  backgroundColor: '$transparent',
                }}
                onPress={() => setVisible(false)}>
                <Icon.X size="$3" color="black" />
              </Button>
            </Select.Label>
            <Sheet.ScrollView>
              <Adapt.Contents />
            </Sheet.ScrollView>
          </Sheet.Frame>
        </Sheet>
      </Adapt>

      <Select.Content>
        <Select.Viewport>
          <Select.Group padding={0}>
            {sortOptions.map((opt, idx) => (
              <SortItem
                key={String(opt.key ?? DEFAULT_KEY)}
                optionKey={opt.key}
                label={opt.label}
                isActive={sort === opt.key}
                onSelect={handleSelect}
                index={idx}
              />
            ))}
          </Select.Group>
        </Select.Viewport>
      </Select.Content>
    </Select>
  )
}
